import { Icon } from '@iconify/react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import styles from './HorizontalScrollWithArrows.module.css'

interface HorizontalScrollWithArrowsProps {
  children: ReactNode
  scrollClassName?: string
}

const SCROLL_OVERFLOW_THRESHOLD = 2

function canScrollHorizontally(element: HTMLDivElement): boolean {
  return element.scrollWidth > element.clientWidth + SCROLL_OVERFLOW_THRESHOLD
}

export function HorizontalScrollWithArrows({
  children,
  scrollClassName,
}: HorizontalScrollWithArrowsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    const { scrollLeft, scrollWidth, clientWidth } = element
    const overflow = canScrollHorizontally(element)

    setHasOverflow(overflow)
    setCanScrollLeft(overflow && scrollLeft > SCROLL_OVERFLOW_THRESHOLD)
    setCanScrollRight(
      overflow && scrollLeft + clientWidth < scrollWidth - SCROLL_OVERFLOW_THRESHOLD
    )
  }, [])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    updateScrollState()
    element.addEventListener('scroll', updateScrollState, { passive: true })

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(element)
    for (const child of element.children) {
      if (child instanceof Element) {
        resizeObserver.observe(child)
      }
    }

    return () => {
      element.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState])

  useLayoutEffect(() => {
    updateScrollState()
    const frame = requestAnimationFrame(updateScrollState)
    return () => cancelAnimationFrame(frame)
  })

  useEffect(() => {
    const onViewportChange = () => updateScrollState()

    window.addEventListener('resize', onViewportChange)

    return () => {
      window.removeEventListener('resize', onViewportChange)
    }
  }, [updateScrollState])

  const scrollHorizontally = (direction: -1 | 1) => {
    const element = scrollRef.current
    if (!element) return

    const distance = Math.max(200, Math.round(element.clientWidth * 0.6))
    element.scrollBy({ left: direction * distance, behavior: 'smooth' })
  }

  const scrollClasses = [
    styles.scroll,
    hasOverflow ? styles.scrollWithArrows : '',
    scrollClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.wrapper}>
      {hasOverflow && canScrollLeft ? <span className={styles.edgeFadeLeft} aria-hidden /> : null}
      {hasOverflow && canScrollRight ? <span className={styles.edgeFadeRight} aria-hidden /> : null}

      {hasOverflow && canScrollLeft ? (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowLeft}`}
          onClick={() => scrollHorizontally(-1)}
          aria-label="Rolar tabela para a esquerda"
        >
          <Icon icon="mdi:chevron-left" aria-hidden />
        </button>
      ) : null}

      <div ref={scrollRef} className={scrollClasses}>
        {children}
      </div>

      {hasOverflow && canScrollRight ? (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowRight}`}
          onClick={() => scrollHorizontally(1)}
          aria-label="Rolar tabela para a direita"
        >
          <Icon icon="mdi:chevron-right" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
